import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Plus,
  Search,
  LayoutGrid,
  LayoutList,
  ArrowUpDown,
  Loader2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { format, differenceInDays } from "date-fns";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DealDetailDrawer } from "@/components/crm/DealDetailDrawer";
import { CreateDealDialog } from "@/components/crm/CreateDealDialog";
import {
  DEAL_STAGES,
  STAGE_LABELS,
  STAGE_BADGE_COLORS,
  formatDealSize,
  normalizeLegacyStage,
} from "@/lib/deal-stages";

interface Deal {
  id: string;
  name: string;
  entity?: string;
  stage: string;
  type?: string;
  asset_class?: string;
  size?: number;
  probability?: number;
  close_date?: string;
  contact_id?: string;
  updated_at: string;
  created_at: string;
  contacts?: { full_name: string } | null;
}

// ─── Sortable Deal Card ─────────────────────────────────────────────────────

function SortableDealCard({
  deal,
  onClick,
}: {
  deal: Deal;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: deal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const daysInStage = differenceInDays(new Date(), new Date(deal.updated_at));

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
      onClick={(e) => {
        // Only open drawer if not dragging
        if (!isDragging) onClick();
      }}
    >
      <div className="space-y-1.5">
        <h4 className="font-medium text-sm leading-tight">{deal.name}</h4>
        {deal.contacts?.full_name && (
          <p className="text-xs text-muted-foreground truncate">
            {deal.contacts.full_name}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {deal.size ? (
            <span className="text-xs font-medium">{formatDealSize(deal.size)}</span>
          ) : null}
          {deal.asset_class && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {deal.asset_class}
            </Badge>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">
          {daysInStage}d in stage
        </p>
      </div>
    </Card>
  );
}

// ─── Kanban Column ──────────────────────────────────────────────────────────

function KanbanColumn({
  stageValue,
  stageLabel,
  deals,
  onDealClick,
}: {
  stageValue: string;
  stageLabel: string;
  deals: Deal[];
  onDealClick: (id: string) => void;
}) {
  const totalValue = deals.reduce((sum, d) => sum + (d.size || 0), 0);

  return (
    <div className="flex flex-col min-w-[220px] max-w-[280px] flex-shrink-0">
      <div className="flex items-center justify-between mb-2 px-1">
        <h3 className="font-medium text-sm">{stageLabel}</h3>
        <Badge variant="secondary" className="text-xs">
          {deals.length}
        </Badge>
      </div>
      {totalValue > 0 && (
        <p className="text-xs text-muted-foreground mb-2 px-1">
          {formatDealSize(totalValue)}
        </p>
      )}
      <div
        className="space-y-2 min-h-[200px] p-2 border-2 border-dashed rounded-md flex-1 overflow-y-auto max-h-[calc(100vh-320px)]"
        data-stage={stageValue}
      >
        <SortableContext
          items={deals.map((d) => d.id)}
          strategy={verticalListSortingStrategy}
        >
          {deals.map((deal) => (
            <SortableDealCard
              key={deal.id}
              deal={deal}
              onClick={() => onDealClick(deal.id)}
            />
          ))}
        </SortableContext>
        {deals.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8">
            No deals
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

type SortField = "name" | "stage" | "size" | "updated_at" | "close_date" | "asset_class";
type SortDir = "asc" | "desc";

export default function PipelineDeals() {
  const queryClient = useQueryClient();
  const { orgId, familyId, isMFO, families } = useTenant();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"kanban" | "table">("kanban");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("updated_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  // Current user for deal creation
  const { data: userId } = useQuery({
    queryKey: ["current-user-id"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    },
  });

  // Fetch deals with TanStack Query
  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deals", orgId, familyId],
    queryFn: async () => {
      let query = supabase
        .from("deals")
        .select("*, contacts:contact_id(full_name)")
        .eq("org_id", orgId!)
        .order("updated_at", { ascending: false });

      if (familyId) query = query.eq("family_id", familyId);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Deal[];
    },
    enabled: !!orgId,
  });

  // Fetch contacts for CreateDealDialog
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts-for-deals", orgId, familyId],
    queryFn: async () => {
      let query = supabase
        .from("contacts")
        .select("id, full_name, organization")
        .eq("org_id", orgId!);
      if (familyId) query = query.eq("family_id", familyId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  // Stage update mutation
  const updateStage = useMutation({
    mutationFn: async ({ dealId, newStage }: { dealId: string; newStage: string }) => {
      const { error } = await supabase
        .from("deals")
        .update({ stage: newStage, updated_at: new Date().toISOString() })
        .eq("id", dealId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["deals-summary"] });
      toast.success("Deal stage updated");
    },
    onError: () => toast.error("Failed to update deal stage"),
  });

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // Determine which column a deal was dropped into
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over) return;

      const dealId = active.id as string;
      const deal = deals.find((d) => d.id === dealId);
      if (!deal) return;

      // Check if dropped over another deal — find the target deal's stage
      const overDeal = deals.find((d) => d.id === over.id);
      if (overDeal && overDeal.stage !== deal.stage) {
        updateStage.mutate({ dealId, newStage: overDeal.stage });
      } else if (!overDeal) {
        // Dropped over a column container via data-stage attribute
        // This is handled by the closestCenter algorithm landing on deals
      }
    },
    [deals, updateStage]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  // Filtering
  const filteredDeals = deals.filter((d) => {
    const matchesSearch =
      !searchQuery ||
      d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.entity?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.asset_class?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.contacts?.full_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const normalizedStage = normalizeLegacyStage(d.stage);
    const matchesStage = stageFilter === "all" || normalizedStage === stageFilter;

    return matchesSearch && matchesStage;
  });

  // Sorting (table only)
  const sortedDeals = [...filteredDeals].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    switch (sortField) {
      case "name":
        return a.name.localeCompare(b.name) * dir;
      case "stage": {
        const stageOrder = DEAL_STAGES.map((s) => s.value);
        const aIdx = stageOrder.indexOf(normalizeLegacyStage(a.stage));
        const bIdx = stageOrder.indexOf(normalizeLegacyStage(b.stage));
        return (aIdx - bIdx) * dir;
      }
      case "size":
        return ((a.size || 0) - (b.size || 0)) * dir;
      case "updated_at":
        return (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) * dir;
      case "close_date": {
        const aDate = a.close_date ? new Date(a.close_date).getTime() : 0;
        const bDate = b.close_date ? new Date(b.close_date).getTime() : 0;
        return (aDate - bDate) * dir;
      }
      case "asset_class":
        return (a.asset_class || "").localeCompare(b.asset_class || "") * dir;
      default:
        return 0;
    }
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleDealClick = (id: string) => {
    setSelectedDealId(id);
    setDrawerOpen(true);
  };

  const handleDealUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ["deals"] });
    queryClient.invalidateQueries({ queryKey: ["deals-summary"] });
  };

  const activeDeal = activeDragId ? deals.find((d) => d.id === activeDragId) : null;

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Deal Pipeline</h1>
        <p className="text-muted-foreground">
          Track investment opportunities from sourcing to closing
        </p>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {DEAL_STAGES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-1 border rounded-md p-0.5">
          <Button
            variant={viewMode === "kanban" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("kanban")}
            className="h-7 px-2"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={viewMode === "table" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("table")}
            className="h-7 px-2"
          >
            <LayoutList className="h-3.5 w-3.5" />
          </Button>
        </div>

        <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          New Deal
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : viewMode === "kanban" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {DEAL_STAGES.map((stage) => {
              const stageDeals = filteredDeals.filter(
                (d) => normalizeLegacyStage(d.stage) === stage.value
              );
              return (
                <KanbanColumn
                  key={stage.value}
                  stageValue={stage.value}
                  stageLabel={stage.label}
                  deals={stageDeals}
                  onDealClick={handleDealClick}
                />
              );
            })}
          </div>
          <DragOverlay>
            {activeDeal ? (
              <Card className="p-3 shadow-lg w-[240px]">
                <h4 className="font-medium text-sm">{activeDeal.name}</h4>
                {activeDeal.size && (
                  <p className="text-xs text-muted-foreground">
                    {formatDealSize(activeDeal.size)}
                  </p>
                )}
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                  <div className="flex items-center gap-1">
                    Deal Name
                    {sortField === "name" && <ArrowUpDown className="h-3 w-3" />}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("stage")}>
                  <div className="flex items-center gap-1">
                    Stage
                    {sortField === "stage" && <ArrowUpDown className="h-3 w-3" />}
                  </div>
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("asset_class")}>
                  <div className="flex items-center gap-1">
                    Asset Class
                    {sortField === "asset_class" && <ArrowUpDown className="h-3 w-3" />}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer text-right" onClick={() => handleSort("size")}>
                  <div className="flex items-center gap-1 justify-end">
                    Size
                    {sortField === "size" && <ArrowUpDown className="h-3 w-3" />}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("close_date")}>
                  <div className="flex items-center gap-1">
                    Close Date
                    {sortField === "close_date" && <ArrowUpDown className="h-3 w-3" />}
                  </div>
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort("updated_at")}>
                  <div className="flex items-center gap-1">
                    Last Updated
                    {sortField === "updated_at" && <ArrowUpDown className="h-3 w-3" />}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDeals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {searchQuery || stageFilter !== "all"
                      ? "No deals match your filters"
                      : "No deals yet. Create your first deal to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                sortedDeals.map((deal) => {
                  const normalizedStage = normalizeLegacyStage(deal.stage);
                  return (
                    <TableRow
                      key={deal.id}
                      className="cursor-pointer"
                      onClick={() => handleDealClick(deal.id)}
                    >
                      <TableCell className="font-medium">{deal.name}</TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={STAGE_BADGE_COLORS[normalizedStage] || ""}
                        >
                          {STAGE_LABELS[normalizedStage] || deal.stage}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {deal.contacts?.full_name || "\u2014"}
                      </TableCell>
                      <TableCell>{deal.asset_class || "\u2014"}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatDealSize(deal.size)}
                      </TableCell>
                      <TableCell>
                        {deal.close_date
                          ? format(new Date(deal.close_date), "PP")
                          : "\u2014"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {differenceInDays(new Date(), new Date(deal.updated_at))}d ago
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Empty state for kanban */}
      {!isLoading && viewMode === "kanban" && deals.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p className="mb-2">No deals in your pipeline yet.</p>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Create your first deal
          </Button>
        </div>
      )}

      {/* Deal detail drawer */}
      <DealDetailDrawer
        dealId={selectedDealId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onUpdate={handleDealUpdate}
        orgId={orgId || undefined}
        isMFO={isMFO}
        familyId={familyId || undefined}
        families={families?.map(f => ({ id: f.id, name: f.name })) || []}
      />

      {/* Create deal dialog */}
      {orgId && userId && (
        <CreateDealDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSuccess={handleDealUpdate}
          orgId={orgId}
          clientId={userId}
          familyId={familyId || undefined}
          contacts={contacts}
        />
      )}
    </div>
  );
}
